import { useState, useEffect } from "react"
import { IconDeviceDesktop } from "@tabler/icons-react"

const DESKTOP_BREAKPOINT = 1024

export function DesktopOnlyGuard({ children }: { children: React.ReactNode }) {
  const [isDesktop, setIsDesktop] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT)
    }

    // Initial check
    checkIsDesktop()

    // Listen for resize events
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`)
    const handleChange = () => {
      checkIsDesktop()
    }

    mql.addEventListener("change", handleChange)
    window.addEventListener("resize", checkIsDesktop)

    return () => {
      mql.removeEventListener("change", handleChange)
      window.removeEventListener("resize", checkIsDesktop)
    }
  }, [])

  // Show nothing while checking (prevents flash)
  if (isDesktop === undefined) {
    return null
  }

  // Show desktop-only message if not desktop
  if (!isDesktop) {
    return (
      <div className="fixed inset-0 bg-[#FFFFFF] flex items-center justify-center p-[24px]">
        <div className="max-w-[480px] w-full text-center space-y-[24px]">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="size-[64px] rounded-full bg-[#F2EEFF] flex items-center justify-center">
              <IconDeviceDesktop className="size-[32px] text-[#654EDA]" />
            </div>
          </div>

          {/* Heading */}
          <div className="space-y-[8px]">
            <h1 className="text-[24px] font-semibold text-[#08090A] leading-[32px]">
              Desktop Experience Required
            </h1>
            <p className="text-[14px] text-[#8A8F98] leading-[20px]">
              For the best experience, please access this application from a desktop or laptop computer with a screen width of at least 1024px.
            </p>
          </div>

          {/* Details */}
          <div className="bg-[#F7F8F8] rounded-[6px] p-[16px] space-y-[12px] text-left">
            <p className="text-[13px] text-[#08090A] leading-[20px]">
              This application is optimized for desktop environments to provide you with:
            </p>
            <ul className="space-y-[8px] list-none">
              <li className="flex items-start gap-[8px]">
                <span className="text-[#654EDA] mt-[2px]">•</span>
                <span className="text-[13px] text-[#08090A] leading-[20px]">
                  Full-featured data visualization and analytics
                </span>
              </li>
              <li className="flex items-start gap-[8px]">
                <span className="text-[#654EDA] mt-[2px]">•</span>
                <span className="text-[13px] text-[#08090A] leading-[20px]">
                  Optimal performance for complex workflows
                </span>
              </li>
              <li className="flex items-start gap-[8px]">
                <span className="text-[#654EDA] mt-[2px]">•</span>
                <span className="text-[13px] text-[#08090A] leading-[20px]">
                  Enhanced productivity with keyboard shortcuts and precise interactions
                </span>
              </li>
            </ul>
          </div>

          {/* Footer note */}
          <p className="text-[12px] text-[#8A8F98] leading-[16px]">
            If you're on a desktop and seeing this message, please ensure your browser window is at least 1024px wide.
          </p>
        </div>
      </div>
    )
  }

  // Render children if desktop
  return <>{children}</>
}

